import { gql, useSubscription } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useStartCalculations } from '../mutations/useStartCalculations.mutation';
import type {
  CalculationResultSubscription,
  CalculationResultSubscriptionVariables,
  PropertyBrandWithProducts,
} from '../types/types';
import { BrandEnum } from 'src/common/enums';

export const CALCULATION_RESULT = gql`
  subscription CalculationResult($subscriptionId: String!) {
    calculationResult(subscriptionId: $subscriptionId) {
      subscriptionId
      brandCode
      isLast
      error
      result {
        _id
        code
        ptsLogo
        name
        products {
          _id
          code
          name
          actionLabel
          productTypeSpecific {
            coverages {
              code
              label
              description
              disables
            }
            modifiers {
              field
              fixed
              min
              max
              default
            }
          }
          partnerId {
            contractor
            insuredPerson
          }
          calculation {
            calculationId
            sessionId
            packageCode
            packageName
            totalPremium
            installmentsPremium
            singlePremium
            singleCalculationId
            installmentsCalculationId
            policyStartDate
            policyDuration
            buildingQuote
            householdQuote
            coverages {
              code
              name
              description
              premium
              insuredAmount
              risks {
                code
                name
                description
                premium
                insuredAmount
                claims {
                  name
                  premium
                  insuredAmount
                }
              }
            }
            additionalCoverages {
              code
              name
              description
              premium
              insuredAmount
              risks {
                code
                name
                description
                premium
                insuredAmount
                claims {
                  name
                  premium
                  insuredAmount
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Subscribes to `calculationResult` events, accumulates brands as they arrive,
 * and triggers `startCalculations` via the mutation hook after the subscription
 * is registered (hook order + eager WS; see code comments).
 *
 * @param subscriptionId — client-generated UUID; subscription is skipped when null.
 */
export function useGetCalculations(subscriptionId: string | null) {
  const [brands, setBrands] = useState<Partial<PropertyBrandWithProducts>[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const { error: subError } = useSubscription<CalculationResultSubscription, CalculationResultSubscriptionVariables>(
    CALCULATION_RESULT,
    {
      variables: { subscriptionId: subscriptionId! },
      skip: !subscriptionId,
      onData: ({ data: subResult }) => {
        const ev = subResult?.data?.calculationResult;
        if (!ev) return;

        if (ev.result) {
          setBrands((prev) => {
            const newCodes = new Set(ev.result!.map((b) => b.code));
            return [...prev.filter((b) => b.code && !newCodes.has(b.code)), ...ev.result!];
          });
        }

        if (ev.isLast) {
          setBrands((prev) => prev.filter((b) => b?.products && b.products.length > 0));
          setIsComplete(true);
        }
      }
    },
  );

  const { data: mutData, error: mutError } = useStartCalculations(subscriptionId);

  useEffect(() => {
    if (!mutData?.startCalculations) return;
    const newResults = mutData.startCalculations.map((ev) => ev.brandCode);
    setIsComplete(false);
    setBrands((prev) => {
      const placeholders: Partial<PropertyBrandWithProducts>[] = newResults
        .filter((code) => !prev.find(p => p.code === code))
        .map((code) => ({ code, name: code, products: [] }));

      const prevMapped: Partial<PropertyBrandWithProducts>[] = prev.map((prod) => ([BrandEnum.WIENER, BrandEnum.GRAWE].includes(prod.code as BrandEnum) ? { ...prod, products: [] } : prod))

      return [...prevMapped, ...placeholders];
    });
  }, [mutData]);

  const loadingBrands = useMemo(
    () => new Set(brands.filter((b) => b?.products && b.products.length === 0).map((b) => b.code)),
    [brands],
  );

  return {
    calculation: brands.length > 0 ? brands : undefined,
    loadingBrands,
    loading: !isComplete && !!subscriptionId,
    error: mutError ?? subError ?? undefined,
  };
}
